
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface AvatarUploadProps {
  avatarUrl: string;
  setAvatarUrl: (url: string) => void;
  username: string;
}

export const AvatarUpload = ({ avatarUrl, setAvatarUrl, username }: AvatarUploadProps) => {
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) {
        throw new Error("You must select an image to upload.");
      }

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const filePath = `${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      setAvatarUrl(data.publicUrl);
      toast.success("Avatar uploaded successfully");
    } catch (error) {
      toast.error("Error uploading avatar");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <div className="relative h-20 w-20">
        <img
          src={avatarUrl || "/placeholder.svg"}
          alt="Avatar"
          className="rounded-full h-20 w-20 object-cover"
        />
        <div className="absolute inset-0 rounded-full bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
          <label className="cursor-pointer">
            <span className="sr-only">Change avatar</span>
            <input
              type="file"
              accept="image/*"
              onChange={uploadAvatar}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? (
              <Loader2 className="h-6 w-6 text-white animate-spin" />
            ) : (
              <span className="text-white text-sm">Change</span>
            )}
          </label>
        </div>
      </div>
      <div>
        <h2 className="text-xl font-semibold">{username || "Username"}</h2>
        <p className="text-sm text-gray-500">Click the image to change your avatar</p>
      </div>
    </div>
  );
};
